import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrowLeft.svg'
import s from '../styles/Reports.module.scss'

const MaterialIcon = ({ children, className = '' }) => (
	<span className={`material-icons-outlined ${className}`.trim()} aria-hidden="true">
		{children}
	</span>
)

export const ReportsShell = ({
	title,
	description,
	backTo,
	actions,
	badge,
	controls,
	children,
}) => {
	const navigate = useNavigate()
	const [downloadNotice, setDownloadNotice] = useState(null)
	const [showCompactControls, setShowCompactControls] = useState(false)
	const pageRef = useRef(null)
	const headerRef = useRef(null)
	useEffect(() => {
		let timeout
		const onDownload = event => {
			setDownloadNotice(event.detail)
			clearTimeout(timeout)
			timeout = setTimeout(() => setDownloadNotice(null), 4200)
		}
		window.addEventListener('kandor-report-download', onDownload)
		return () => { window.removeEventListener('kandor-report-download', onDownload); clearTimeout(timeout) }
	}, [])

	useEffect(() => {
		const page = pageRef.current
		if (!page || !controls) return undefined
		const updateCompactControls = () => {
			const headerHeight = headerRef.current?.offsetHeight || 0
			setShowCompactControls(page.scrollTop >= Math.max(headerHeight - 8, 1))
		}
		page.addEventListener('scroll', updateCompactControls, { passive: true })
		updateCompactControls()
		return () => page.removeEventListener('scroll', updateCompactControls)
	}, [controls])

	return (
		<div className={s.page} ref={pageRef}>
			<div className={`${s.pageHeader} ${controls ? s.pageHeaderWithControls : ''}`.trim()} ref={headerRef}>
				<div className={s.pageHeaderMain}>
					{backTo && (
						<button
							type="button"
							className={s.backButton}
							onClick={() => navigate(backTo)}
						>
							<ArrowIcon />
							<span>Back</span>
						</button>
					)}
					<div className={s.titleRow}>
						<h1 className={s.pageTitle}>{title}</h1>
						{badge && <span className={s.previewBadge}>{badge}</span>}
					</div>
					{description && <p className={s.pageDescription}>{description}</p>}
				</div>
				{actions && <div className={s.pageActions}>{actions}</div>}
				{controls && <div className={s.pageHeaderControls}>{controls}</div>}
			</div>
			{controls && showCompactControls && <div className={s.compactReportControls}>{controls}</div>}
			<div className={s.pageBody}>{children}</div>
			{downloadNotice && <div className={s.downloadToast} role="status"><MaterialIcon>check_circle</MaterialIcon><div><strong>Download ready</strong><span>{downloadNotice.filename} · {downloadNotice.rowCount} rows</span></div></div>}
		</div>
	)
}

export const Icon = MaterialIcon

export const MetricCard = ({ label, value, detail, icon, tone = 'purple' }) => (
	<article className={`${s.metricCard} ${s[`metricCard--${tone}`] || ''}`.trim()}>
		<div className={s.metricCardTop}>
			<div className={s.metricIcon}>
				<MaterialIcon>{icon}</MaterialIcon>
			</div>
			<span className={s.metricLabel}>{label}</span>
		</div>
		<strong className={s.metricValue}>{value}</strong>
		{detail && <span className={s.metricDetail}>{detail}</span>}
	</article>
)

export const StatusPill = ({ children, tone }) => {
	const resolvedTone = tone || String(children || '').toLowerCase().replace(/\s+/g, '-')
	return (
		<span className={`${s.statusPill} ${s[`statusPill--${resolvedTone}`] || ''}`.trim()}>
			{children}
		</span>
	)
}

export const ReportToolbar = ({ period, periods, onPeriodChange, onExport, exportLabel = 'Export CSV', contextLabel = 'Academic year 2026-27', children }) => (
	<div className={s.toolbar}>
		<div className={s.toolbarContext}>
			<div><span>Report context</span><strong>{contextLabel}</strong></div>
		</div>
		<div className={s.toolbarActions}>
			<div className={s.toolbarFilters}>
				<label className={s.fieldLabel}>
					<span>Reporting period</span>
					<select value={period} onChange={event => onPeriodChange(event.target.value)}>
						{periods.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</label>
				{children}
			</div>
			<button type="button" className={s.primaryButton} onClick={onExport}>
				<MaterialIcon>download</MaterialIcon>
				<span>{exportLabel}</span>
			</button>
		</div>
	</div>
)

const DESTINATION_ICONS = {
	Exploring: 'travel_explore',
	Shortlisted: 'bookmark_added',
	'In progress': 'edit_note',
	Applied: 'send',
	Accepted: 'verified',
	Decided: 'flag_circle',
	Enrolled: 'school',
	'Decided / enrolled': 'flag_circle',
}

export const DestinationFunnel = ({ rows }) => (
	<div className={s.funnel}>
		{rows.map(row => (
			<div key={row.stage} className={s.funnelRow}>
				<div className={s.funnelLabel}>
					<span className={s.funnelStage}><MaterialIcon>{DESTINATION_ICONS[row.stage] || 'explore'}</MaterialIcon>{row.stage}</span>
					<strong>{row.count}</strong>
				</div>
				<div className={s.funnelTrack}>
					<div
						className={`${s.funnelFill} ${s[`funnelFill--${row.tone}`] || ''}`.trim()}
						style={{ width: `${row.percent}%` }}
					/>
				</div>
			</div>
		))}
	</div>
)
